import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import { OneOfOption } from '@/types/oneOfOption';

interface Props {
  readonly label: string;
  readonly defaultValue?: string;
  readonly value?: string;
  readonly options: readonly OneOfOption[];
  readonly name?: string;

  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  onValueChange?(value: string): void;
}

export const OneOf: React.FC<Props> = React.forwardRef<HTMLButtonElement, Props>(function Either(
  props: Props,
  ref: React.Ref<HTMLButtonElement>,
): React.ReactElement {
  const { value, defaultValue, label, options, name, onValueChange, onChange } = props;
  const tr = useTranslator();

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;

      onValueChange?.(value);
      onChange?.(event);
    },
    [onChange, onValueChange],
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Typography fontSize={16} fontWeight={400} lineHeight="24px">
        {label}
      </Typography>
      <RadioGroup value={value} defaultValue={defaultValue} onChange={handleChange}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {options.map(
            (option: OneOfOption): React.ReactElement => (
              <FormControlLabel
                key={option.value}
                sx={styles.control}
                control={
                  <Box ml={-1}>
                    <Radio ref={ref} name={name} value={option.value} tabIndex={0} />
                  </Box>
                }
                label={
                  <Typography fontSize={16} whiteSpace="nowrap" color="text.primary">
                    {tr(option.label)}
                  </Typography>
                }
              />
            ),
          )}
        </Box>
      </RadioGroup>
    </Box>
  );
});

const styles: Record<string, SystemStyleObject> = {
  control: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    width: 264,
    height: 24,
  },
};
